/**
 * My Ticket & QR Page
 * Shows user's event ticket with QR code for check-in
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Ticket, 
  Calendar, 
  Clock, 
  MapPin, 
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronLeft,
  Download,
  Share2,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Local storage key prefix for QR cache
const QR_CACHE_PREFIX = 'campassed_qr_';

export default function MyTicket() {
  const { passId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  
  const [ticket, setTicket] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Generate QR code from data
  const generateQRCode = useCallback(async (qrData) => {
    try {
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      return dataUrl;
    } catch (err) {
      console.error('QR generation error:', err);
      return null;
    }
  }, []);
  
  // Cache QR code to localStorage
  const cacheQRCode = useCallback((passId, dataUrl) => {
    try {
      localStorage.setItem(`${QR_CACHE_PREFIX}${passId}`, dataUrl);
    } catch (err) {
      console.error('Cache error:', err);
    }
  }, []);
  
  // Get cached QR code
  const getCachedQRCode = useCallback((passId) => {
    try {
      return localStorage.getItem(`${QR_CACHE_PREFIX}${passId}`);
    } catch (err) {
      return null;
    }
  }, []);
  
  // Fetch ticket data
  useEffect(() => {
    const fetchTicket = async () => {
      if (!passId || !session?.access_token) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Try to get cached QR first for instant display
        const cachedQR = getCachedQRCode(passId);
        if (cachedQR) {
          setQrDataUrl(cachedQR);
        }
        
        // Fetch ticket details
        const response = await fetch(`${API_URL}/api/tickets/${passId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch ticket');
        }
        
        const data = await response.json();
        setTicket(data);
        
        // Generate and cache QR code
        if (data.qr_code_data) {
          const qrUrl = await generateQRCode(data.qr_code_data);
          if (qrUrl) {
            setQrDataUrl(qrUrl);
            cacheQRCode(passId, qrUrl);
          }
        }
      } catch (err) {
        console.error('Ticket fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTicket();
  }, [passId, session?.access_token, generateQRCode, getCachedQRCode, cacheQRCode]);
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Format time
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // Handle both "HH:MM:SS" and ISO formats
    if (timeStr.includes('T')) {
      return new Date(timeStr).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  // Get age verification badge
  const getAgeVerificationBadge = () => {
    if (!ticket?.events?.age_verification_required) {
      return null; // Not required, don't show badge
    }
    
    const status = ticket?.age_verification_status?.status;
    const eventId = ticket?.events?.id;
    const attendeeId = ticket?.event_attendee_id;
    
    switch (status) {
      case 'verified':
        return (
          <div className="flex items-center gap-2 px-4 py-2 bg-cool-mint/10 border border-cool-mint/30 rounded-xl">
            <ShieldCheck className="w-5 h-5 text-cool-mint" />
            <span className="text-cool-mint font-medium">Age Verified</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-2 px-4 py-2 bg-soft-amber/10 border border-soft-amber/30 rounded-xl">
            <Shield className="w-5 h-5 text-soft-amber" />
            <span className="text-soft-amber font-medium">Verification Pending</span>
          </div>
        );
      case 'action_needed':
      case 'not_started':
      default:
        return (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 px-4 py-2 bg-coral-red/10 border border-coral-red/30 rounded-xl">
              <ShieldAlert className="w-5 h-5 text-coral-red" />
              <span className="text-coral-red font-medium">Age Verification Required</span>
            </div>
            <Button 
              variant="outline"
              className="border-coral-red text-coral-red hover:bg-coral-red/10"
              onClick={() => navigate(`/verify/age?event=${eventId}&attendee=${attendeeId}`)}
            >
              Verify Now
            </Button>
          </div>
        );
    }
  };
  
  // Download QR code
  const handleDownload = () => {
    if (!qrDataUrl) return;
    
    const link = document.createElement('a');
    link.download = `ticket-${passId}.png`;
    link.href = qrDataUrl;
    link.click();
  };
  
  // Share ticket
  const handleShare = async () => {
    if (!ticket) return;
    
    const event = ticket.events;
    const shareData = {
      title: `My Ticket - ${event?.event_name}`,
      text: `I'm attending ${event?.event_name} on ${formatDate(event?.date)}!`,
      url: window.location.href
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Share error:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin mx-auto mb-4" />
          <p className="text-soft-silver">Loading your ticket...</p>
        </div>
      </div>
    );
  }
  
  if (error || !ticket) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="bg-graphite/50 border-coral-red/30 max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-12 h-12 text-coral-red mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-cloud-white mb-2">Ticket Not Found</h2>
            <p className="text-soft-silver mb-4">{error || "This ticket doesn't exist or has been cancelled."}</p>
            <Button onClick={() => navigate('/app/my-tickets')}>
              View All Tickets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const event = ticket.events || {};
  const org = event.organizations || {};
  
  return (
    <div className="max-w-2xl mx-auto" data-testid="my-ticket-page">
      {/* Back Button */}
      <Link 
        to="/app/my-tickets" 
        className="inline-flex items-center gap-2 text-soft-silver hover:text-cloud-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span>Back to My Tickets</span>
      </Link>
      
      {/* Ticket Card */}
      <Card className="bg-graphite/50 border-white/[0.08] overflow-hidden">
        {/* Event Header */}
        <div className="bg-gradient-to-r from-electric-ice-blue/20 to-cool-mint/20 p-6 border-b border-white/[0.08]">
          <div className="flex items-start justify-between">
            <div>
              <Badge className="bg-electric-ice-blue/20 text-electric-ice-blue border-0 mb-2">
                {org.name || 'Event'}
              </Badge>
              <h1 className="text-2xl font-bold text-cloud-white mb-2">
                {event.event_name || 'Event Ticket'}
              </h1>
              <div className="flex flex-wrap gap-4 text-soft-silver text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{formatTime(event.start_time)}</span>
                  {event.end_time && <span>- {formatTime(event.end_time)}</span>}
                </div>
              </div>
              {event.location && (
                <div className="flex items-center gap-2 text-soft-silver text-sm mt-2">
                  <MapPin className="w-4 h-4" />
                  <span>{event.location}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" onClick={handleDownload} title="Download QR">
                <Download className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleShare} title="Share">
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
        
        <CardContent className="p-6 space-y-6">
          {/* QR Code */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-2xl shadow-lg mb-4">
              {qrDataUrl ? (
                <img 
                  src={qrDataUrl} 
                  alt="Ticket QR Code" 
                  className="w-[250px] h-[250px] min-w-[200px] min-h-[200px]"
                  data-testid="ticket-qr-code"
                />
              ) : (
                <div className="w-[250px] h-[250px] flex items-center justify-center bg-gray-100 rounded-lg">
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
              )}
            </div>
            
            {/* Ticket ID */}
            <div className="text-center">
              <p className="text-xs text-muted-text uppercase tracking-wider mb-1">Ticket ID</p>
              <p className="font-mono text-lg text-cloud-white tracking-wider" data-testid="ticket-id">
                {ticket.id}
              </p>
            </div>
          </div>
          
          {/* Divider */}
          <div className="border-t border-dashed border-white/[0.15]" />
          
          {/* Age Verification Badge */}
          <div className="flex justify-center">
            {getAgeVerificationBadge()}
          </div>
          
          {/* Ticket Status */}
          <div className="flex justify-center">
            <div className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full",
              ticket.status === 'active' 
                ? "bg-cool-mint/10 text-cool-mint" 
                : ticket.status === 'checked_in'
                ? "bg-electric-ice-blue/10 text-electric-ice-blue"
                : "bg-muted-text/10 text-muted-text"
            )}>
              {ticket.status === 'active' && <CheckCircle2 className="w-4 h-4" />}
              {ticket.status === 'checked_in' && <Ticket className="w-4 h-4" />}
              {ticket.status === 'cancelled' && <XCircle className="w-4 h-4" />}
              <span className="font-medium capitalize">{ticket.status?.replace('_', ' ')}</span>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="bg-obsidian/50 rounded-xl p-4">
            <h3 className="text-sm font-medium text-cloud-white mb-2">How to use your ticket</h3>
            <ol className="text-sm text-soft-silver space-y-1 list-decimal list-inside">
              <li>Show this QR code at the event entrance</li>
              <li>Door staff will scan to verify your ticket</li>
              <li>Your ticket is valid for one-time entry only</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
